import type { Diagnostic } from "@codemirror/lint";

/**
 * Detector de segredos (plugin "Detector de segredos"): padrões públicos dos provedores e um
 * genérico para senha/token atribuído a uma string. Roda só no navegador.
 */

export interface SecretRule {
  name: string;
  re: RegExp;
  severity: Diagnostic["severity"];
  /** Regra genérica: valores de exemplo ("changeme", "<sua-chave>") não contam. */
  generic?: boolean;
}

/** Padrões públicos dos provedores; o genérico pega senha/token atribuído a uma string. */
export const SECRET_RULES: SecretRule[] = [
  { name: "chave de acesso da AWS", re: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g, severity: "error" },
  {
    name: "segredo da AWS",
    re: /aws.{0,20}(?:secret|private).{0,20}['"][0-9a-zA-Z/+]{40}['"]/gi,
    severity: "error",
  },
  {
    name: "token do GitHub",
    re: /\b(?:gh[pousr]_[A-Za-z0-9]{36,255}|github_pat_[A-Za-z0-9_]{60,255})\b/g,
    severity: "error",
  },
  { name: "token do GitLab", re: /\bglpat-[A-Za-z0-9_-]{20,}\b/g, severity: "error" },
  { name: "token do Slack", re: /\bxox[baprs]-[A-Za-z0-9-]{10,}\b/g, severity: "error" },
  { name: "chave secreta do Stripe", re: /\b(?:sk|rk)_live_[A-Za-z0-9]{20,}\b/g, severity: "error" },
  { name: "chave de API do Google", re: /\bAIza[0-9A-Za-z_-]{35}\b/g, severity: "error" },
  { name: "chave da Anthropic", re: /\bsk-ant-[A-Za-z0-9_-]{20,}\b/g, severity: "error" },
  { name: "chave da OpenAI", re: /\bsk-(?!ant-)(?:proj-)?[A-Za-z0-9_-]{32,}\b/g, severity: "error" },
  {
    name: "chave privada",
    re: /-----BEGIN (?:RSA |EC |DSA |OPENSSH |PGP |ENCRYPTED )?PRIVATE KEY-----/g,
    severity: "error",
  },
  {
    name: "JWT",
    re: /\beyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
    severity: "warning",
  },
  {
    name: "URL de banco com senha",
    re: /\b(?:postgres(?:ql)?|mysql|mariadb|mongodb(?:\+srv)?|redis|amqps?):\/\/[^:\s'"/]+:[^@\s'"]{3,}@/gi,
    severity: "warning",
    generic: true,
  },
  {
    name: "senha ou token no código",
    re: /\b(?:password|passwd|senha|secret|api[_-]?key|access[_-]?token|auth[_-]?token|client[_-]?secret)\b["']?\s*[:=]\s*["'][^"'\s]{8,}["']/gi,
    severity: "warning",
    generic: true,
  },
];

const PLACEHOLDER =
  /(?:changeme|change-me|example|exemplo|placeholder|your[_-]|seu[_-]|xxx|\*\*\*|<[^>]+>|\$\{|process\.env|os\.environ|getenv)/i;

export function findSecrets(text: string): Array<{ from: number; to: number; rule: SecretRule }> {
  const found: Array<{ from: number; to: number; rule: SecretRule }> = [];
  for (const rule of SECRET_RULES) {
    rule.re.lastIndex = 0;
    for (let m = rule.re.exec(text); m; m = rule.re.exec(text)) {
      if (rule.generic && PLACEHOLDER.test(m[0])) continue;
      if (found.some((f) => m!.index < f.to && m!.index + m[0].length > f.from)) continue;
      found.push({ from: m.index, to: m.index + m[0].length, rule });
    }
  }
  return found;
}
